import React, { useState } from 'react';
import './NodeLabelsRenderer.less';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Button, Input, Space, Tag, Typography, message } from 'antd';
import { NodeLabelObj } from 'types/capacityManagement.types';

const { Text } = Typography;

const defaultNodeLabels: NodeLabelObj = {
  analytics: '',
  loader: '',
} as const;

interface EditingState {
  key: string;
  value: string;
}

interface NewLabelState {
  key: string;
  value: string;
}

function NodeLabelsRenderer({
  isCreateNewSize,
  currentNodeLabelRow,
  onNodeLabelsChangeForm,
}: {
  isCreateNewSize: boolean;
  currentNodeLabelRow: {
    label: NodeLabelObj;
  };
  onNodeLabelsChangeForm: ({
    selectedNodeLabels,
  }: {
    selectedNodeLabels: Record<string, string>;
  }) => void;
}) {
  // Initialize with default labels if empty
  const currentLabels = currentNodeLabelRow.label || defaultNodeLabels;

  const [editingLabel, setEditingLabel] = useState<EditingState | null>(null);
  const [newLabel, setNewLabel] = useState<NewLabelState>({
    key: '',
    value: '',
  });
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Get tag color based on label key
  const getTagColor = (key: string) => {
    switch (key) {
      case 'analytics':
        return 'geekblue';
      case 'loader':
        return 'green';
      default:
        return 'default';
    }
  };

  // Check if a label is protected (cannot be deleted)
  const isProtectedLabel = (key: string) =>
    ['analytics', 'loader'].includes(key);

  // Handle saving an edited label
  const handleSaveEdit = () => {
    if (
      !editingLabel ||
      !editingLabel.key.trim() ||
      !editingLabel.value.trim()
    ) {
      message.error('Both label key and value are required');
      return;
    }

    const updatedLabels = { ...currentLabels };
    updatedLabels[editingLabel.key] = editingLabel.value;

    onNodeLabelsChangeForm({ selectedNodeLabels: updatedLabels });
    setEditingLabel(null);
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingLabel(null);
  };

  // Handle adding a new label
  const handleAddNew = () => {
    if (!newLabel.key.trim() || !newLabel.value.trim()) {
      message.error('Both label key and value are required');
      return;
    }

    if (currentLabels[newLabel.key]) {
      message.error('Label key already exists');
      return;
    }

    const updatedLabels = {
      ...currentLabels,
      [newLabel.key]: newLabel.value,
    };

    onNodeLabelsChangeForm({ selectedNodeLabels: updatedLabels });
    setNewLabel({ key: '', value: '' });
    setIsAddingNew(false);
  };

  // Handle deleting a label
  const handleDelete = (key: string) => {
    if (isProtectedLabel(key)) {
      message.error(`Cannot delete protected label: ${key}`);
      return;
    }

    const updatedLabels = { ...currentLabels };
    delete updatedLabels[key];

    onNodeLabelsChangeForm({ selectedNodeLabels: updatedLabels });
  };

  return (
    <div className="node-labels-renderer">
      <div className="node-labels-renderer__header"></div>

      <div className="node-labels-renderer__content">
        {Object.entries(currentLabels).map(([key, value]) => (
          <div key={key} className="node-labels-renderer__item">
            {editingLabel?.key === key ? (
              // Edit mode
              <div className="node-labels-renderer__edit-form">
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="Label key"
                    value={editingLabel.key}
                    onChange={e =>
                      setEditingLabel({ ...editingLabel, key: e.target.value })
                    }
                    disabled={isProtectedLabel(key)}
                    style={{ width: '40%' }}
                  />
                  <Input
                    placeholder="Label value"
                    value={editingLabel.value}
                    onChange={e =>
                      setEditingLabel({
                        ...editingLabel,
                        value: e.target.value,
                      })
                    }
                    style={{ width: '40%' }}
                  />
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveEdit}
                    style={{ width: '10%' }}
                  />
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancelEdit}
                    style={{ width: '10%' }}
                  />
                </Space.Compact>
              </div>
            ) : (
              // Display mode
              <div className="node-labels-renderer__display">
                <Tag
                  color={getTagColor(key)}
                  className="node-labels-renderer__tag"
                >
                  <strong>{key}</strong>: {value}
                </Tag>
                <Space>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => setEditingLabel({ key, value })}
                  />
                  {!isProtectedLabel(key) && (
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(key)}
                      danger
                    />
                  )}
                </Space>
              </div>
            )}
          </div>
        ))}

        {isAddingNew && (
          <div className="node-labels-renderer__item">
            <div className="node-labels-renderer__add-form">
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="Label key"
                  value={newLabel.key}
                  onChange={e =>
                    setNewLabel({ ...newLabel, key: e.target.value })
                  }
                  style={{ width: '40%' }}
                />
                <Input
                  placeholder="Label value"
                  value={newLabel.value}
                  onChange={e =>
                    setNewLabel({ ...newLabel, value: e.target.value })
                  }
                  style={{ width: '40%' }}
                />
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleAddNew}
                  style={{ width: '10%' }}
                />
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setIsAddingNew(false);
                    setNewLabel({ key: '', value: '' });
                  }}
                  style={{ width: '10%' }}
                />
              </Space.Compact>
            </div>
          </div>
        )}

        {Object.keys(currentLabels).length === 0 && !isAddingNew && (
          <div className="node-labels-renderer__empty">
            <Text type="secondary">
              No node labels configured. Click "Add Label" to get started.
            </Text>
          </div>
        )}

        {!isCreateNewSize && (
          <div className="node-labels-renderer__add-button">
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setIsAddingNew(true)}
              disabled={isAddingNew}
            >
              Add Label
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default NodeLabelsRenderer;
